<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { MaskInput } from 'maska'
import { useFormStore } from '@/stores/form'

const emit = defineEmits<{
  (e: 'submit'): void
  (e: 'validate'): void
  (e: 'showTcpa'): void
}>()

const formStore = useFormStore()

const phone = ref(formStore.data.phone || '')
const phoneUnmasked = ref('')
const isComplete = ref(false)
const showValidation = ref(false)
const inputRef = ref<HTMLInputElement | null>(null)

// Initialize maska on input
watch(inputRef, (el) => {
  if (el) {
    new MaskInput(el, {
      mask: '(###) ###-####',
      eager: true,
      onMaska: (detail) => {
        phoneUnmasked.value = detail.unmasked
        isComplete.value = detail.completed
      },
    })
  }
}, { immediate: true })

// Handle browser autofill - check after a short delay for autofilled values
onMounted(() => {
  // Check multiple times for autofill (browsers fill at different times)
  const checkAutofill = () => {
    if (inputRef.value) {
      const currentValue = inputRef.value.value
      if (currentValue && currentValue !== phone.value) {
        phone.value = currentValue
        // Trigger input event to make maska process the value
        inputRef.value.dispatchEvent(new Event('input', { bubbles: true }))
      }
    }
  }

  // Check immediately, after 100ms, and after 500ms (covers most autofill timing)
  nextTick(checkAutofill)
  setTimeout(checkAutofill, 100)
  setTimeout(checkAutofill, 500)
})

const isChecking = computed(() => formStore.validation.phone.checking)
const validationError = computed(() => formStore.validation.phone.error)

function handleSubmit() {
  if (!isComplete.value) {
    showValidation.value = true
    return
  }

  formStore.setField('phone', phone.value)
  formStore.setField('phoneUnmasked', phoneUnmasked.value)

  // Show TCPA drawer
  emit('showTcpa')
}

function handleKeydown(e: KeyboardEvent) {
  if (e.key === 'Enter') {
    handleSubmit()
  }
}
</script>

<template>
  <div class="w-full animate-slide-up">
    <div class="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mx-1">
      <!-- Phone input -->
      <div>
        <input
          ref="inputRef"
          v-model="phone"
          type="tel"
          inputmode="numeric"
          placeholder="(555) 555-5555"
          autocomplete="tel"
          :disabled="isChecking"
          @keydown="handleKeydown"
          :class="[
            'w-full py-3.5 px-4 border-2 rounded-xl text-lg transition-colors tracking-wide',
            (showValidation && !isComplete) || validationError
              ? 'border-red-400 bg-red-50'
              : 'border-gray-200 focus:border-primary'
          ]"
        />
        <p v-if="showValidation && !isComplete" class="text-red-500 text-xs mt-1 ml-1">
          Please enter a valid phone number
        </p>
        <p v-else-if="validationError" class="text-red-500 text-xs mt-1 ml-1">
          {{ validationError }}
        </p>
      </div>

      <!-- Helper text -->
      <p class="text-xs text-gray-500 mt-2 ml-1">
        No spam calls. Ever.
      </p>

      <!-- Submit button -->
      <button
        @click="handleSubmit"
        :disabled="isChecking"
        class="w-full mt-5 py-4 bg-primary text-white rounded-full font-semibold text-base shadow-lg shadow-primary/30 hover:bg-primary-dark active:scale-[0.98] transition-all disabled:opacity-70 disabled:cursor-not-allowed"
      >
        <template v-if="isChecking">
          <svg class="animate-spin inline w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Verifying...
        </template>
        <template v-else>
          See Results
          <svg class="inline w-5 h-5 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </template>
      </button>
    </div>
  </div>
</template>
